import React, { useState } from 'react';
import { 
  CogIcon,
  CurrencyDollarIcon,
  TruckIcon,
  CreditCardIcon,
  ShieldCheckIcon,
  ExclamationTriangleIcon,
  TrashIcon,
  CheckCircleIcon,
  XCircleIcon
} from '@heroicons/react/24/outline';
import TaxSettings from '../../components/admin/TaxSettings';
import axios from '../../utils/axios';

const SystemCleanup = () => {
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [confirmed, setConfirmed] = useState(false);

  const handleCleanup = async () => {
    if (!confirmed) return;

    setLoading(true);
    setResult(null);
    setError(null);

    try {
      const response = await axios.post('/api/admin/system-cleanup', { password });
      setResult(response.data);
      setPassword('');
      setConfirmed(false);
    } catch (err) {
      setError(err.response?.data?.message || 'שגיאה בביצוע הניקוי');
    } finally {
      setLoading(false);
    }
  };

  const deletedLabels = {
    return_photos: 'תמונות החזרות',
    return_shipments: 'משלוחי החזרות',
    return_refunds: 'החזרים כספיים',
    return_exchanges: 'החלפות',
    return_timelines: 'היסטוריית החזרות',
    return_items: 'פריטי החזרות',
    returns: 'החזרות',
    payment_documents: 'מסמכי תשלום',
    payment_orders: 'שיוך תשלומים להזמנות',
    checks: "צ'קים",
    payments: 'תשלומים',
    order_tracking: 'מעקב הזמנות',
    order_warnings: 'התראות הזמנות',
    order_items: 'פריטי הזמנות',
    orders: 'הזמנות',
    price_negotiation_messages: 'הודעות משא ומתן',
    price_negotiation_offers: 'הצעות משא ומתן',
    price_negotiation_items: 'פריטי משא ומתן',
    price_negotiations: 'משא ומתן מחירים',
    credit_transactions: 'תנועות אשראי',
    credit_approval_requests: 'בקשות אישור אשראי',
    store_credit_transactions: 'תנועות קרדיט חנות',
    inventory_transactions: 'תנועות מלאי',
    sales_targets: 'יעדי מכירות',
    cart_items: 'פריטי עגלה',
    carts: 'עגלות',
    agent_draft_carts: 'טיוטות סוכנים',
    wishlists: 'רשימות משאלות',
    product_reviews: 'ביקורות מוצרים',
    notifications: 'התראות',
    user_journeys: 'מסעות משתמש'
  };

  const resetLabels = {
    products: 'מוצרים (מלאי, מכירות, דירוג)',
    bundles: 'חבילות (מכירות, דירוג)',
    company_credits: 'אשראי חברות (מגבלה: 50,000)',
    companies: 'חברות (מגבלת אשראי: 50,000)',
    store_credits: 'קרדיט חנות (יתרה: 0)',
    promotions: 'מבצעים (שימושים: 0)',
    order_templates: 'תבניות הזמנה (שימושים: 0)'
  };

  return (
    <div className="space-y-6" dir="rtl">
      {/* Warning Banner */}
      <div className="bg-red-50 border-2 border-red-300 rounded-lg p-6">
        <div className="flex items-start">
          <ExclamationTriangleIcon className="h-8 w-8 text-red-600 ml-4 flex-shrink-0 mt-0.5" />
          <div>
            <h3 className="text-lg font-bold text-red-800">
              ניקוי נתוני מערכת
            </h3>
            <p className="mt-2 text-sm text-red-700">
              פעולה זו תמחק את כל הנתונים העסקיים מהמערכת ולא ניתן לשחזר אותם.
              הפעולה תשפיע על:
            </p>
            <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <h4 className="font-semibold text-red-800 text-sm mb-1">יימחק לצמיתות:</h4>
                <ul className="text-xs text-red-700 space-y-0.5 list-disc list-inside">
                  <li>כל ההזמנות והפריטים שלהן</li>
                  <li>כל התשלומים, הצ'קים והמסמכים</li>
                  <li>כל ההחזרות והחזרים</li>
                  <li>היסטוריית אשראי ותנועות חובה</li>
                  <li>תנועות מלאי</li>
                  <li>יעדי מכירות וביצועי סוכנים</li>
                  <li>עגלות, משאלות, ביקורות, התראות</li>
                  <li>משא ומתן מחירים</li>
                  <li>נתוני אנליטיקס</li>
                </ul>
              </div>
              <div>
                <h4 className="font-semibold text-green-800 text-sm mb-1">יישמר:</h4>
                <ul className="text-xs text-green-700 space-y-0.5 list-disc list-inside">
                  <li>מוצרים (פרטים מלאים, מלאי יתאפס)</li>
                  <li>חברות (אשראי יתאפס ל-50,000)</li>
                  <li>סוכנים ושיוך חברות</li>
                  <li>משתמשים ומנהלים</li>
                  <li>קטגוריות, מותגים, ספקים</li>
                  <li>חוזי מחירים ומבצעים (מונים יתאפסו)</li>
                  <li>תבניות הזמנה (מונים יתאפסו)</li>
                  <li>הזמנות רכש וקבלות סחורה</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Action Card */}
      <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
        <h4 className="text-base font-semibold text-gray-900 mb-4">ביצוע ניקוי</h4>
        
        <div className="space-y-4">
          {/* Password Input */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              סיסמת אישור
            </label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="הזן סיסמת ניקוי"
              className="w-full max-w-xs px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-red-500 focus:border-red-500 text-sm"
              disabled={loading}
            />
          </div>

          {/* Confirmation Checkbox */}
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={confirmed}
              onChange={(e) => setConfirmed(e.target.checked)}
              className="h-4 w-4 text-red-600 border-gray-300 rounded focus:ring-red-500"
              disabled={loading}
            />
            <span className="text-sm text-gray-700">
              אני מאשר/ת שאני מבין/ה שפעולה זו בלתי הפיכה ותמחק את כל הנתונים העסקיים
            </span>
          </label>

          {/* Execute Button */}
          <button
            onClick={handleCleanup}
            disabled={!password || !confirmed || loading}
            className={`inline-flex items-center px-6 py-2.5 rounded-md text-sm font-medium transition-colors ${
              !password || !confirmed || loading
                ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                : 'bg-red-600 text-white hover:bg-red-700 focus:ring-2 focus:ring-red-500 focus:ring-offset-2'
            }`}
          >
            {loading ? (
              <>
                <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                מבצע ניקוי...
              </>
            ) : (
              <>
                <TrashIcon className="h-4 w-4 ml-2" />
                בצע ניקוי מערכת
              </>
            )}
          </button>
        </div>

        {/* Error */}
        {error && (
          <div className="mt-4 bg-red-50 border border-red-200 rounded-lg p-4 flex items-start">
            <XCircleIcon className="h-5 w-5 text-red-500 ml-2 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-red-700">{error}</p>
          </div>
        )}

        {/* Success Result */}
        {result && result.success && (
          <div className="mt-4 space-y-4">
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-start">
              <CheckCircleIcon className="h-5 w-5 text-green-500 ml-2 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-green-800">{result.message}</p>
                <p className="text-xs text-green-600 mt-1">
                  נמחקו {result.summary.total_deleted.toLocaleString()} רשומות | 
                  אופסו {result.summary.total_reset.toLocaleString()} רשומות
                </p>
              </div>
            </div>

            {/* Detailed Results */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Deleted */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h5 className="text-sm font-semibold text-gray-800 mb-2">רשומות שנמחקו</h5>
                <div className="space-y-1">
                  {Object.entries(result.details.deleted).map(([key, value]) => (
                    <div key={key} className="flex justify-between text-xs">
                      <span className="text-gray-600">{deletedLabels[key] || key}</span>
                      <span className={`font-medium ${value > 0 ? 'text-red-600' : 'text-gray-400'}`}>
                        {value.toLocaleString()}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Reset */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h5 className="text-sm font-semibold text-gray-800 mb-2">רשומות שאופסו</h5>
                <div className="space-y-1">
                  {Object.entries(result.details.reset).map(([key, value]) => (
                    <div key={key} className="flex justify-between text-xs">
                      <span className="text-gray-600">{resetLabels[key] || key}</span>
                      <span className={`font-medium ${value > 0 ? 'text-blue-600' : 'text-gray-400'}`}>
                        {value.toLocaleString()}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const AdminSettings = () => {
  const [activeTab, setActiveTab] = useState('tax');

  const tabs = [
    {
      id: 'tax',
      name: 'הגדרות מס ומחירים',
      icon: CurrencyDollarIcon,
      description: 'ניהול שיעורי מס, משלוח ומחירים'
    },
    {
      id: 'shipping',
      name: 'הגדרות משלוח',
      icon: TruckIcon,
      description: 'ניהול עלויות משלוח וסף חינם'
    },
    {
      id: 'payment',
      name: 'אמצעי תשלום',
      icon: CreditCardIcon,
      description: 'ניהול אמצעי תשלום זמינים'
    },
    {
      id: 'security',
      name: 'אבטחה',
      icon: ShieldCheckIcon,
      description: 'הגדרות אבטחה ומשתמשים'
    },
    {
      id: 'advanced',
      name: 'מתקדם',
      icon: CogIcon,
      description: 'הגדרות מתקדמות וניקוי מערכת'
    }
  ];

  const renderTabContent = () => {
    switch (activeTab) {
      case 'tax':
        return <TaxSettings />;
      case 'shipping':
        return (
          <div className="bg-white rounded-lg shadow-md p-6" dir="rtl">
            <h3 className="text-lg font-medium text-gray-900 mb-4">הגדרות משלוח</h3>
            <p className="text-gray-600">הגדרות משלוח מפורטות יופיעו כאן</p>
          </div>
        );
      case 'payment':
        return (
          <div className="bg-white rounded-lg shadow-md p-6" dir="rtl">
            <h3 className="text-lg font-medium text-gray-900 mb-4">אמצעי תשלום</h3>
            <p className="text-gray-600">ניהול אמצעי תשלום יופיע כאן</p>
          </div>
        );
      case 'security':
        return (
          <div className="bg-white rounded-lg shadow-md p-6" dir="rtl">
            <h3 className="text-lg font-medium text-gray-900 mb-4">הגדרות אבטחה</h3>
            <p className="text-gray-600">הגדרות אבטחה יופיעו כאן</p>
          </div>
        );
      case 'advanced':
        return <SystemCleanup />;
      default:
        return <TaxSettings />;
    }
  };

  return (
    <div className="space-y-6" dir="rtl">
      {/* Header */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="flex items-center mb-4">
          <CogIcon className="h-8 w-8 ml-3 text-blue-500" />
          <div>
            <h1 className="text-2xl font-bold text-gray-900">הגדרות מערכת</h1>
            <p className="text-gray-600">ניהול הגדרות כלליות של המערכת</p>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-lg shadow-md">
        <div className="border-b border-gray-200">
          <nav className="flex space-x-8 space-x-reverse px-6">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                    activeTab === tab.id
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <div className="flex items-center">
                    <Icon className="h-5 w-5 ml-2" />
                    {tab.name}
                  </div>
                </button>
              );
            })}
          </nav>
        </div>

        {/* Tab Content */}
        <div className="p-6">
          {renderTabContent()}
        </div>
      </div>
    </div>
  );
};

export default AdminSettings;
